<template>
  <v-container fluid class="pa-6">

    <!-- ══════════════════════════════════════════════════════
         SUPER ADMIN PLATFORM DASHBOARD
         ══════════════════════════════════════════════════════ -->
    <template v-if="isSuperAdmin">

      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-6 flex-wrap" style="gap:12px">
        <div>
          <h1 class="page-heading">Platform Overview</h1>
          <p class="text-body-2 text-medium-emphasis mt-1">
            System-wide statistics — all schools, agents, and revenue
          </p>
        </div>
        <v-btn variant="outlined" rounded="lg" prepend-icon="mdi-refresh" :loading="saLoading" @click="loadPlatformStats">
          Refresh
        </v-btn>
      </div>

      <div v-if="saLoading && !saStats" class="empty-state">
        <v-progress-circular indeterminate color="#667de8" size="48" />
        <p class="text-body-2 text-medium-emphasis mt-4">Loading platform data…</p>
      </div>

      <template v-else-if="saStats">

        <!-- KPI Cards Row 1: Entities -->
        <v-row class="mb-4">
          <v-col cols="6" sm="3">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">{{ saStats.schools.total }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Schools</div>
                  <div class="text-caption text-medium-emphasis">{{ saStats.schools.approved }} approved · {{ saStats.schools.pending }} pending</div>
                </div>
                <v-avatar color="#ede9fe" size="48" rounded="lg">
                  <v-icon color="#7c3aed" size="24">mdi-school-outline</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">{{ saStats.users.admins }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Admins</div>
                  <div class="text-caption text-medium-emphasis">{{ saStats.users.staff }} staff · {{ saStats.users.parents }} parents</div>
                </div>
                <v-avatar color="#dbeafe" size="48" rounded="lg">
                  <v-icon color="#2563eb" size="24">mdi-account-tie-outline</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">{{ saStats.agents.total }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Agents</div>
                  <div class="text-caption text-medium-emphasis">{{ saStats.agents.running }} running · {{ saStats.agents.stopped }} stopped</div>
                </div>
                <v-avatar color="#dcfce7" size="48" rounded="lg">
                  <v-icon color="#16a34a" size="24">mdi-robot-outline</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">{{ saStats.revenue.active_subscriptions }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Active Subs</div>
                  <div class="text-caption text-medium-emphasis">{{ saStats.revenue.trial_count }} on trial</div>
                </div>
                <v-avatar color="#fef9c3" size="48" rounded="lg">
                  <v-icon color="#ca8a04" size="24">mdi-credit-card-check-outline</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- KPI Cards Row 2: Revenue -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">R {{ fmtMoney(saStats.revenue.monthly_revenue) }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Monthly Recurring Revenue</div>
                  <div class="text-caption text-medium-emphasis">from {{ saStats.revenue.active_subscriptions }} active subscriptions</div>
                </div>
                <v-avatar color="#dcfce7" size="56" rounded="lg">
                  <v-icon color="#16a34a" size="28">mdi-cash-multiple</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold stat-value">R {{ fmtMoney(saStats.revenue.total_revenue) }}</div>
                  <div class="text-body-2 text-medium-emphasis mt-1">Total Billed (all agents)</div>
                  <div class="text-caption text-medium-emphasis">including inactive &amp; trial</div>
                </div>
                <v-avatar color="#f3e8ff" size="56" rounded="lg">
                  <v-icon color="#9333ea" size="28">mdi-chart-line</v-icon>
                </v-avatar>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Agent Health + Transaction History -->
        <v-row>

          <!-- Agent Health -->
          <v-col cols="12" lg="7">
            <v-card rounded="xl" elevation="0" border>
              <v-card-title class="pa-4 pb-2 d-flex align-center justify-space-between">
                <span class="section-title">Agent Health</span>
                <div class="d-flex" style="gap:6px">
                  <v-chip size="x-small" color="success" variant="tonal">{{ saStats.agents.running }} running</v-chip>
                  <v-chip size="x-small" color="error" variant="tonal" v-if="saStats.agents.errored > 0">{{ saStats.agents.errored }} error</v-chip>
                  <v-chip size="x-small" color="default" variant="tonal">{{ saStats.agents.stopped }} stopped</v-chip>
                </div>
              </v-card-title>
              <v-divider />

              <div v-if="saStats.agentHealth.length === 0" class="pa-8 text-center text-medium-emphasis text-body-2">
                No agents found across the platform.
              </div>

              <v-list v-else lines="two" class="pa-0">
                <v-list-item
                  v-for="agent in saStats.agentHealth"
                  :key="agent.id"
                  class="px-4"
                >
                  <template #prepend>
                    <v-avatar :color="agentStatusColor(agent.status).bg" size="36" rounded="lg" class="mr-3">
                      <v-icon :color="agentStatusColor(agent.status).icon" size="18">{{ agentStatusIcon(agent.status) }}</v-icon>
                    </v-avatar>
                  </template>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ agent.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ agent.school_name || '(no school)' }}</v-list-item-subtitle>
                  <template #append>
                    <div class="text-right">
                      <v-chip :color="agentStatusColor(agent.status).chip" size="x-small" variant="tonal" class="mb-1">
                        {{ agent.status || 'unknown' }}
                      </v-chip>
                      <div class="text-caption text-medium-emphasis">
                        {{ agent.payment_status === 'active' ? 'R ' + fmtMoney(agent.payment_amount) + '/mo' : agent.payment_status || '—' }}
                      </div>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <!-- Transaction History -->
          <v-col cols="12" lg="5">
            <v-card rounded="xl" elevation="0" border>
              <v-card-title class="pa-4 pb-2">
                <span class="section-title">Transaction History</span>
              </v-card-title>
              <v-divider />

              <div v-if="saStats.transactions.length === 0" class="pa-8 text-center text-medium-emphasis text-body-2">
                No payment records yet.
              </div>

              <v-list v-else lines="two" class="pa-0">
                <v-list-item
                  v-for="tx in saStats.transactions"
                  :key="tx.id"
                  class="px-4"
                >
                  <template #prepend>
                    <v-avatar :color="paymentChipColor(tx.payment_status).bg" size="36" rounded="lg" class="mr-3">
                      <v-icon :color="paymentChipColor(tx.payment_status).icon" size="18">mdi-credit-card-outline</v-icon>
                    </v-avatar>
                  </template>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ tx.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ tx.school_name || '—' }} · {{ fmtDate(tx.updated_at) }}</v-list-item-subtitle>
                  <template #append>
                    <div class="text-right">
                      <div class="text-body-2 font-weight-bold" :style="{ color: tx.payment_status === 'active' ? '#16a34a' : '#6b7280' }">
                        R {{ fmtMoney(tx.payment_amount) }}
                      </div>
                      <v-chip :color="paymentChipColor(tx.payment_status).chip" size="x-small" variant="tonal">
                        {{ tx.payment_status }}
                      </v-chip>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

        </v-row>

      </template>
    </template>

    <!-- ══════════════════════════════════════════════════════
         SCHOOL-LEVEL AGENT DASHBOARD (admin / contributor)
         ══════════════════════════════════════════════════════ -->
    <template v-else>

      <!-- Page Header -->
      <div class="d-flex align-center justify-space-between mb-6 flex-wrap" style="gap:12px">
        <div>
          <h1 class="page-heading">ClassMom Agents</h1>
          <p class="text-body-2 text-medium-emphasis mt-1">
            {{ isContributor ? 'Your assigned AI classroom assistants' : 'Manage AI classroom assistants running as OpenClaw Services' }}
          </p>
        </div>
        <v-btn
          v-if="!isContributor"
          color="#667de8"
          variant="flat"
          rounded="lg"
          prepend-icon="mdi-plus"
          @click="showAddDialog = true"
        >Add ClassMom</v-btn>
      </div>

      <!-- Stats Row -->
      <v-row class="mb-6">
        <v-col cols="6" sm="3" v-for="stat in stats" :key="stat.label">
          <v-card rounded="xl" elevation="0" border class="stat-card pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h4 font-weight-bold stat-value">{{ stat.value }}</div>
                <div class="text-body-2 text-medium-emphasis mt-1">{{ stat.label }}</div>
              </div>
              <v-avatar :color="stat.bgColor" size="48" rounded="lg">
                <v-icon :color="stat.iconColor" size="24">{{ stat.icon }}</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Status checking indicator -->
      <v-alert
        v-if="statusChecking && agents.length > 0 && !isContributor"
        type="info"
        variant="tonal"
        density="compact"
        rounded="lg"
        class="mb-4"
        icon="mdi-sync"
      >
        <div class="d-flex align-center" style="gap:10px">
          <v-progress-circular indeterminate size="16" width="2" color="info" />
          <span class="text-body-2">Checking live agent statuses…</span>
        </div>
      </v-alert>

      <!-- Loading -->
      <div v-if="loading" class="empty-state">
        <v-progress-circular indeterminate color="#667de8" size="48" />
        <p class="text-body-2 text-medium-emphasis mt-4">Loading agents...</p>
      </div>

      <!-- Agent Cards -->
      <v-row v-else-if="agents.length > 0">
        <v-col
          cols="12" sm="6" lg="4" xl="3"
          v-for="agent in agents"
          :key="agent.id"
        >
          <ClassMomCard
            :agent="agent"
            @start="startAgent"
            @stop="stopAgent"
            @edit="openEdit"
            @delete="confirmDelete"
            @pair="openPair"
            @view="viewAgent"
          />
        </v-col>
      </v-row>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <v-icon size="72" color="grey-lighten-2">mdi-robot-off-outline</v-icon>
        <template v-if="isContributor">
          <h3 class="text-h6 text-medium-emphasis mt-4">No agents assigned to you yet</h3>
          <p class="text-body-2 text-disabled mt-2">
            Ask your administrator to assign you to a ClassMom agent.
          </p>
        </template>
        <template v-else>
          <h3 class="text-h6 text-medium-emphasis mt-4">No ClassMom agents yet</h3>
          <p class="text-body-2 text-disabled mt-2 mb-6">
            Add your first ClassMom to get started. Each agent runs as an independent OpenClaw Service.
          </p>
          <v-btn color="#667de8" variant="flat" rounded="lg" prepend-icon="mdi-plus" @click="showAddDialog = true">
            Add Your First ClassMom
          </v-btn>
        </template>
      </div>

      <!-- Dialogs -->
      <AddClassMomDialog v-model="showAddDialog" @created="onAgentCreated" />

      <WaPairingDialog
        v-model="showPairDialog"
        :agent="selectedAgent"
        @paired="onPaired"
      />

      <!-- Delete Confirm -->
      <v-dialog v-model="showDeleteDialog" max-width="400">
        <v-card rounded="xl">
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4" style="gap:12px">
              <v-avatar color="error" size="44" rounded="lg">
                <v-icon color="white">mdi-delete-outline</v-icon>
              </v-avatar>
              <div>
                <h3 class="font-weight-bold" style="color:#1a1a2e">Remove Agent</h3>
                <p class="text-body-2 text-medium-emphasis">This cannot be undone</p>
              </div>
            </div>
            <p class="text-body-2">
              Are you sure you want to remove
              <strong>{{ agentToDelete?.name }}</strong>?
              The OpenClaw Service profile will be deactivated.
            </p>
          </v-card-text>
          <v-card-actions class="pa-4 pt-0">
            <v-spacer />
            <v-btn variant="text" rounded="lg" @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" variant="flat" rounded="lg" :loading="deleteLoading" @click="deleteAgent">Remove</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>

    <!-- Snackbar (shared) -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" rounded="lg" location="bottom right">
      {{ snackbar.message }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import ClassMomCard from '@/components/ClassMomCard.vue'
import AddClassMomDialog from '@/components/AddClassMomDialog.vue'
import WaPairingDialog from '@/components/WaPairingDialog.vue'

export default {
  name: 'DashboardView',
  components: { ClassMomCard, AddClassMomDialog, WaPairingDialog },
  setup() {
    const router = useRouter()
    const snackbar = ref({ show: false, message: '', color: 'success' })
    const toast = (message, color = 'success') => { snackbar.value = { show: true, message, color } }

    // ── Role detection ───────────────────────────────────────
    const userRole = computed(() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}').role } catch { return null }
    })
    const isSuperAdmin  = computed(() => userRole.value === 'super_admin')
    const isContributor = computed(() => userRole.value === 'contributor')

    // ══════════════════════════════════════════════════════
    // SUPER ADMIN state
    // ══════════════════════════════════════════════════════
    const saStats = ref(null)
    const saLoading = ref(false)

    const loadPlatformStats = async () => {
      saLoading.value = true
      try {
        const { data } = await axios.get('/api/superAdmin/platform-stats')
        saStats.value = data
      } catch (err) {
        console.error('platform-stats error:', err)
        toast('Failed to load platform data', 'error')
      } finally {
        saLoading.value = false
      }
    }

    const fmtMoney = v => Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const fmtDate = v => v ? new Date(v).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

    const agentStatusColor = status => ({
      running: { bg: '#dcfce7', icon: '#16a34a', chip: 'success' },
      stopped: { bg: '#f3f4f6', icon: '#6b7280', chip: 'default' },
      error:   { bg: '#fee2e2', icon: '#dc2626', chip: 'error' },
    }[status] || { bg: '#f3f4f6', icon: '#6b7280', chip: 'default' })

    const agentStatusIcon = status => ({
      running: 'mdi-play-circle-outline',
      stopped: 'mdi-stop-circle-outline',
      error:   'mdi-alert-circle-outline',
    }[status] || 'mdi-help-circle-outline')

    const paymentChipColor = status => ({
      active:    { bg: '#dcfce7', icon: '#16a34a', chip: 'success' },
      trial:     { bg: '#fef9c3', icon: '#ca8a04', chip: 'warning' },
      inactive:  { bg: '#f3f4f6', icon: '#6b7280', chip: 'default' },
      cancelled: { bg: '#fee2e2', icon: '#dc2626', chip: 'error' },
    }[status] || { bg: '#f3f4f6', icon: '#6b7280', chip: 'default' })

    // ══════════════════════════════════════════════════════
    // SCHOOL-LEVEL state
    // ══════════════════════════════════════════════════════
    const agents = ref([])
    const loading = ref(false)
    const statusChecking = ref(false)
    const slotInfo = ref(null) // null = not yet loaded
    const teacherStats = ref(null)
    const deleteLoading = ref(false)
    const showAddDialog = ref(false)
    const showDeleteDialog = ref(false)
    const showPairDialog = ref(false)
    const selectedAgent = ref(null)
    const agentToDelete = ref(null)

    const loadAgents = async () => {
      loading.value = true
      try {
        const [agentsRes, slotRes] = await Promise.all([
          axios.get('/api/agents'),
          axios.get('/api/agents/slot-check').catch(() => null),
        ])
        agents.value = agentsRes.data.agents
        if (slotRes) slotInfo.value = slotRes.data
      } catch (err) {
        console.error('Failed to load agents:', err)
        toast('Failed to load agents', 'error')
      } finally {
        loading.value = false
      }
      syncLiveStatuses()
    }

    const loadTeacherStats = async () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}')
        const schoolId = u.school_id || u.school?.id
        if (!schoolId) return
        const { data } = await axios.get(`/api/schools/${schoolId}/teacher-stats`, { params: { days: 30 } })
        teacherStats.value = data
      } catch (err) {
        console.warn('Teacher stats load failed:', err.message)
      }
    }

    const syncLiveStatuses = async () => {
      if (agents.value.length === 0) return
      statusChecking.value = true
      try {
        const { data } = await axios.get('/api/agents/live-status')
        for (const agent of agents.value) {
          if (data.statuses[agent.id] !== undefined) {
            agent.status = data.statuses[agent.id]
          }
        }
      } catch (err) {
        console.warn('Live status check failed:', err.message)
      } finally {
        statusChecking.value = false
      }
    }

    const stats = computed(() => {
      // Contributors see activity stats instead of running/stopped counts
      if (isContributor.value && teacherStats.value) {
        const ts = teacherStats.value
        const fmtNum = n => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n)
        return [
          { label: 'Messages Received',  value: fmtNum(ts.message_count),  icon: 'mdi-message-outline',       bgColor: '#e8ecff', iconColor: '#667de8' },
          { label: 'Files Uploaded',     value: fmtNum(ts.files),           icon: 'mdi-file-upload-outline',   bgColor: '#f0fdf4', iconColor: '#16a34a' },
          { label: 'Tokens Used',        value: fmtNum(ts.tokens),          icon: 'mdi-lightning-bolt-outline', bgColor: '#faf5ff', iconColor: '#7c3aed' },
          { label: 'Reminders Sent',     value: fmtNum(ts.reminders_sent),  icon: 'mdi-bell-check-outline',    bgColor: '#fff7ed', iconColor: '#ea580c' },
        ]
      }

      const total   = agents.value.length
      const running = agents.value.filter(a => a.status === 'running').length
      const stopped = agents.value.filter(a => a.status !== 'running').length
      const si      = slotInfo.value
      const avail   = si?.slotsAvailable ?? null

      const slotBg   = avail === 0 ? '#fee2e2' : avail === 1 ? '#fff7ed' : '#e8ecff'
      const slotIcon = avail === 0 ? '#dc2626' : avail === 1 ? '#ea580c' : '#667de8'
      const slotVal  = si ? `${si.slotsUsed}/${si.slotsPurchased}` : '—'

      return [
        { label: 'Total Agents',  value: total,    icon: 'mdi-robot-outline',       bgColor: '#ede9fe', iconColor: '#7c3aed' },
        { label: 'Running',       value: running,  icon: 'mdi-play-circle-outline',  bgColor: '#dcfce7', iconColor: '#16a34a' },
        { label: 'Stopped',       value: stopped,  icon: 'mdi-stop-circle-outline',  bgColor: '#f3f4f6', iconColor: '#6b7280' },
        { label: 'Agent Slots',   value: slotVal,  icon: 'mdi-ticket-outline',       bgColor: slotBg,    iconColor: slotIcon  },
      ]
    })

    const onAgentCreated = agent => {
      agents.value.unshift(agent)
      axios.get('/api/agents/slot-check').then(r => { slotInfo.value = r.data }).catch(() => {})
    }

    const startAgent = async id => {
      try {
        const { data } = await axios.post(`/api/agents/${id}/start`)
        updateLocal(data.agent)
        toast(`${data.agent.name} started`)
        setTimeout(syncLiveStatuses, 2500)
      } catch (err) {
        toast(err.response?.data?.error || 'Failed to start agent', 'error')
      }
    }

    const stopAgent = async id => {
      try {
        const { data } = await axios.post(`/api/agents/${id}/stop`)
        updateLocal(data.agent)
        toast(`${data.agent.name} stopped`, 'warning')
        setTimeout(syncLiveStatuses, 1000)
      } catch (err) {
        toast(err.response?.data?.error || 'Failed to stop agent', 'error')
      }
    }

    const viewAgent = id => router.push({ name: 'agent-detail', params: { id } })
    const openEdit  = () => toast('Edit functionality coming soon', 'info')

    const confirmDelete = id => {
      agentToDelete.value = agents.value.find(a => a.id === id)
      showDeleteDialog.value = true
    }

    const deleteAgent = async () => {
      deleteLoading.value = true
      try {
        await axios.delete(`/api/agents/${agentToDelete.value.id}`)
        const name = agentToDelete.value.name
        agents.value = agents.value.filter(a => a.id !== agentToDelete.value.id)
        showDeleteDialog.value = false
        agentToDelete.value = null
        toast(`${name} removed`)
        axios.get('/api/agents/slot-check').then(r => { slotInfo.value = r.data }).catch(() => {})
      } catch (err) {
        toast(err.response?.data?.error || 'Failed to delete agent', 'error')
      } finally {
        deleteLoading.value = false
      }
    }

    const openPair = id => {
      selectedAgent.value = agents.value.find(a => a.id === id)
      showPairDialog.value = true
    }

    const onPaired = async () => {
      await loadAgents()
      toast('WhatsApp linked successfully!')
    }

    const updateLocal = updated => {
      const idx = agents.value.findIndex(a => a.id === updated.id)
      if (idx !== -1) agents.value[idx] = updated
    }

    // ── Mount / unmount ──────────────────────────────────────
    let statusPollTimer = null
    onMounted(() => {
      if (isSuperAdmin.value) {
        loadPlatformStats()
      } else {
        loadAgents()
        if (isContributor.value) {
          loadTeacherStats()
        } else {
          statusPollTimer = setInterval(syncLiveStatuses, 5 * 60 * 1000)
        }
      }
    })
    onUnmounted(() => clearInterval(statusPollTimer))

    return {
      isSuperAdmin,
      isContributor,
      snackbar,
      // super admin
      saStats, saLoading, loadPlatformStats,
      fmtMoney, fmtDate, agentStatusColor, agentStatusIcon, paymentChipColor,
      // school level
      agents, loading, statusChecking, stats, slotInfo, teacherStats,
      showAddDialog, showDeleteDialog, showPairDialog, deleteLoading,
      selectedAgent, agentToDelete,
      onAgentCreated, startAgent, stopAgent, viewAgent, openEdit,
      confirmDelete, deleteAgent,
      openPair, onPaired,
    }
  }
}
</script>

<style scoped>
.page-heading {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.2;
}
.section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a2e;
}
.stat-card {
  transition: box-shadow 0.2s ease;
}
.stat-card:hover {
  box-shadow: 0 4px 20px rgba(0,0,0,0.07) !important;
}
.stat-value {
  color: #1a1a2e;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 80px 24px;
}
</style>
